import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Blog } from '../types';

export const useBlogs = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);

  // Helper function to safely convert timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    
    // If it's a Firestore timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's a timestamp object with _seconds
    if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      return new Date(timestamp._seconds * 1000);
    }
    
    // If it's already a Date object
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // If it's a number (milliseconds)
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // If it's a string, try to parse it
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    // Fallback to current date
    return new Date();
  };

  // Helper function to process blog data
  const processBlogData = (doc: any): Blog => {
    const blogData = doc.data();
    
    return {
      id: doc.id,
      title: blogData.title || '',
      description: blogData.description || '',
      content: blogData.content || '',
      contentType: blogData.contentType || 'html',
      author: blogData.author || '',
      userId: blogData.userId || '',
      categories: blogData.categories || [],
      tags: blogData.tags || [],
      status: blogData.status || 'published',
      slug: blogData.slug || '',
      link: blogData.link || '',
      metaDescription: blogData.metaDescription || '',
      metaKeywords: blogData.metaKeywords || '',
      readingTime: blogData.readingTime || '5',
      wordCount: blogData.wordCount || 0,
      createdAt: convertTimestamp(blogData.createdAt || blogData.timestamp),
      updatedAt: convertTimestamp(blogData.updatedAt || blogData.timestamp),
      timestamp: convertTimestamp(blogData.timestamp || blogData.createdAt)
    };
  };

  // Fetch blogs from Firestore
  const fetchBlogs = async (filters?: {
    category?: string;
    status?: string;
    search?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);

      let q = query(
        collection(db, 'blogs'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );

      // Apply additional filters
      if (filters?.category && filters.category !== 'all') {
        q = query(q, where('categories', 'array-contains', filters.category));
      }

      // Limit results for pagination
      q = query(q, limit(9));

      const querySnapshot = await getDocs(q);
      const blogsData: Blog[] = [];
      
      querySnapshot.forEach((doc) => {
        const blogData = doc.data();
        
        blogsData.push(processBlogData(doc));
      });

      setBlogs(blogsData);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === 9);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  // Load more blogs (pagination)
  const loadMoreBlogs = async () => {
    if (!hasMore || loading) return;

    try {
      setLoading(true);
      setError(null);

      let q = query(
        collection(db, 'blogs'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc'),
        limit(9)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const additionalBlogs: Blog[] = [];

      querySnapshot.forEach((doc) => {
        const blogData = doc.data();
        
        additionalBlogs.push(processBlogData(doc));
      });

      setBlogs(prev => [...prev, ...additionalBlogs]);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(additionalBlogs.length === 9);
    } catch (err) {
      console.error('Error loading more blogs:', err);
      setError('Failed to load more blogs');
    } finally {
      setLoading(false);
    }
  };

  // Search blogs
  const searchBlogs = async (searchTerm: string) => {
    try {
      setLoading(true);
      setError(null);

      const trimmed = searchTerm.trim();
      if (!trimmed) {
        await fetchBlogs();
        return;
      }

      // Bounded search: fetch latest published items and filter in-memory
      const q = query(
        collection(db, 'blogs'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc'),
        limit(200)
      );

      const querySnapshot = await getDocs(q);
      const allBlogs: Blog[] = [];
      querySnapshot.forEach((doc) => {
        allBlogs.push(processBlogData(doc));
      });

      const s = trimmed.toLowerCase();
      const filteredBlogs = allBlogs.filter(blog =>
        blog.title.toLowerCase().includes(s) ||
        blog.description.toLowerCase().includes(s) ||
        blog.content.toLowerCase().includes(s) ||
        blog.categories.some(cat => String(cat).toLowerCase().includes(s)) ||
        blog.tags.some(tag => String(tag).toLowerCase().includes(s))
      );

      setBlogs(filteredBlogs);
      setLastDoc(null);
      setHasMore(false);
    } catch (err) {
      console.error('Error searching blogs:', err);
      setError('Failed to search blogs');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from blogs
  const getCategories = async (): Promise<string[]> => {
    try {
      const q = query(collection(db, 'blogs'), where('status', '==', 'published'), limit(200));
      
      const querySnapshot = await getDocs(q);
      const categories = new Set<string>();
      
      querySnapshot.forEach((doc) => {
        const blogData = doc.data();
        if (blogData.categories && Array.isArray(blogData.categories)) {
          blogData.categories.forEach((cat: string) => categories.add(cat));
        }
      });
      
      return Array.from(categories).sort();
    } catch (err) {
      console.error('Error fetching categories:', err);
      return [];
    }
  };

  // Refresh blogs
  const refreshBlogs = () => {
    setBlogs([]);
    setLastDoc(null);
    setHasMore(true);
    fetchBlogs();
  };

  // Load blogs on component mount
  useEffect(() => {
    fetchBlogs();
  }, []);

  return {
    blogs,
    loading,
    error,
    hasMore,
    fetchBlogs,
    loadMoreBlogs,
    searchBlogs,
    getCategories,
    refreshBlogs
  };
};
